// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import * as iso3166 from 'i18n-iso-countries'
import React, { useEffect, useState } from "react"
import { useLocales } from "react-localized"
import ReactMarkdown from 'react-markdown'
import {
  IonButton,
  IonCol,
  IonInput,
  IonItem,
  IonItemGroup,
  IonList,
  IonRow,
  IonText
} from "@ionic/react"
import { CountryModalSelector, IonItemInput, VatinFieldValidator } from "../../../lib"
import { SectionModal } from "../../../lib/webid/SectionModal"


interface VATCountrySelectorProps extends React.ComponentProps<typeof IonInput> {
  country: string | null
  setCountry(code: string): void
  setVatNumber(vatNumber: string): void
  vatCountry: string | null
  vatNumber: string | null
}


interface VATSpecialCaseModalProps {
  isOpen: boolean
  setVatCountry: (code: string) => void
}


interface VATNumberModalProps {
  country: string
  errors?: string[]
  isOpen: boolean
  setVatNumber(vatNumber: string): void
  vatCountry: string
  vatNumber: string | null
}


// The ISO 3166 country codes for which we want a VAT number.
const VAT_COUNTRIES: string[] = [
  "AT", "BE", "BG", "CY", "CZ", "DE", "DK", "EE", "ES",
  "FI", "FR", "HR", "HU", "IT", "IE", "LT", "LU", "LV", "MT",
  "PL", "PT", "RO", "SE", "SI", "SK", "NL",

  "EL", // Greece, EU
  "XI", // Northern Ireland, EU
]


const VAT_CODES: {[key: string]: string} = {
  // For VAT administration purposes, the European Commission
  // uses EL and GB for Greece and the United Kingdom respectively.
  GR: "EL",
}

// There are some special cases in the EU
const VAT_SPECIAL_AREAS: string[] = [
  // The code XI is being used by the UK Government,[25] as
  // an EORI number country code prefix for Northern Ireland,
  // and the members of European union for European Union
  // value added tax reports with trade with Northern Ireland
  "GB"
]


const getEuropeanComissionVatCode: (country: string) => string = (country) => {
  return VAT_CODES[country] || country
}


const VATSpecialCaseModal: React.FC<VATSpecialCaseModalProps> = ({
  isOpen,
  setVatCountry
}) => {
  const {gettext} = useLocales()
  return (
    <SectionModal grid isOpen={isOpen}>
      <IonRow className="main">
        <IonCol>
          <IonText>
            <p>
              <ReactMarkdown components={{p: React.Fragment}}>
                {gettext("Is your company located in **Northern Ireland**?")}
              </ReactMarkdown>
            </p>
            <p>
              <ReactMarkdown components={{p: React.Fragment}}>
                {gettext("_Companies in Northern Ireland are subject to the Customs Code of the European Union (EU)._")}
              </ReactMarkdown>
            </p>
          </IonText>
          <IonList lines="inset" style={{cursor: "pointer"}}>
            <IonItem detail onClick={() => setVatCountry("XI")}>{gettext("Yes")}</IonItem>
            <IonItem detail onClick={() => setVatCountry("GB")}>{gettext("No")}</IonItem>
          </IonList>
        </IonCol>
      </IonRow>
      <></>
    </SectionModal>
  )
}


const VATNumberModal: React.FC<VATNumberModalProps> = ({
  errors,
  country,
  setVatNumber,
  vatCountry,
  vatNumber,
  ...props
}) => {
  const {gettext, locale} = useLocales()
  const specialCaseName: {[key: string]: string} = {
    EL: gettext("Greece"),
    XI: gettext("United Kingdom, Northern Ireland")
  }
  const countryName = (!["XI", "EL"].includes(vatCountry))
    ? iso3166.getName(country, locale)
    : specialCaseName[vatCountry]

  // Ensure that we do not use the same VAT number if the country has
  // changed
  const [value, setValue] = useState<string|null>(vatNumber)
  const validator = new VatinFieldValidator({
    code: vatCountry,
    errorMessages: {
      invalid: gettext("The VAT number you entered is not valid."),
      inactive: gettext`The VAT number you entered is not active.`,
      invalidStartCode: gettext`A valid VAT number begins with ${vatCountry}.`,
      invalidCharacters: gettext("A VAT number can not contain these characters.")
    }
  })

  // Reset value if the country changes
  useEffect(() => { setValue(null) }, [vatCountry])

  return (
    <SectionModal grid isOpen={props.isOpen}>
      <IonRow className="main">
        <IonCol>
          <IonText>
              <p>
                <ReactMarkdown components={{p: React.Fragment}}>
                  {gettext("Please enter your VAT number. Do not include periods, dashes or other characters that are not letters or numbers.")}
                </ReactMarkdown>
              </p>
          </IonText>
          <IonList>
            <IonItemInput
              error={(!!errors) ? (validator.getErrorMessage(errors[0]) as string || "invalid") : null}
              helpText={gettext("Click or tap to enter your VAT number")}
              label={gettext`VAT number (${countryName})`}
              onValidated={(isValid, value) => {
                if (!!isValid) {
                  setValue(value)
                }
              }}
              successMessage={(gettext("Appears to be a valid VAT number."))}
              transformers={[
                (v: string) => v.toUpperCase(),
                (v: string) => { return v.replace(/[^A-Z0-9]+/, '') },
              ]}
              validator={validator}
              value={value}
            />
            <IonItem lines="none">
              <IonButton
                className="navigation"
                disabled={!value}
                fill="clear"
                onClick={() => {
                  setVatNumber(value || "ERROR")
                }}
              >
                {gettext("Continue")}
              </IonButton>
            </IonItem>
          </IonList>
        </IonCol>
      </IonRow>
      <></>
    </SectionModal>
  )
}


const VATCountrySelector: React.FC<VATCountrySelectorProps> = ({
  ...props
}) => {
  const needsSpecialArea: (country: string | null) => boolean = (country) => {
    return VAT_SPECIAL_AREAS.includes(country || "")
  }

  const needsVat: (vatCountry: string | null) => boolean = (vatCountry) => {
    return (!vatCountry) ? false : VAT_COUNTRIES.includes(vatCountry)
  }

  const {gettext} = useLocales()
  const [country, setCountry] = useState<string|null>(props.country)
  const [vatNumber, setVatNumber] = useState<string|null>(props.vatNumber)
  const [vatCountry, setVatCountry] = useState<string|null>(
    (!!props.country && !needsSpecialArea(country))
    ? props.country
    : null
  )
  const allowVatEntry = needsVat(vatCountry || country)

  useEffect(() => {
    if (country !== props.vatCountry) {
      setVatCountry(null)
    }
  }, [country])

  useEffect(() => {
    if (props.country !== country) {
      setVatNumber(null)
    }
  }, [country])

  // If we have a value, and the props.vatNumber is null,
  // then the parent wants to reset it and show the prompt.
  useEffect(() => {
    if (props.vatNumber === null && !!vatNumber) {
      setVatNumber(null)
    }
  }, [props.vatNumber])

  useEffect(() => {
    if ((!!country && !!vatNumber) || (!!country && !needsVat(country))) {
      if (!!country) { props.setCountry(country) }
      if (!!vatNumber) { props.setVatNumber(vatNumber) }
    }
  }, [country, vatNumber])
  return (
    <>
      <VATSpecialCaseModal
        isOpen={needsSpecialArea(country) && !vatCountry}
        setVatCountry={(code: string) => setVatCountry(code)}
      />
      <VATNumberModal
        isOpen={needsVat(vatCountry || country) && !vatNumber}
        country={country || ""}
        setVatNumber={setVatNumber}
        vatCountry={vatCountry || country || ""}
        vatNumber={vatNumber}
      />
      <CountryModalSelector
        disabled={props.disabled}
        label={gettext("Organization country")}
        helpText={gettext("Click or tap to select a country")}
        onClosed={(code) => {
          // If the country has not changed, but it has special areas,
          // then the user must answer the question again.
          console.log(code)
          if (code === "GB") {
            setVatCountry(null)
          }
        }}
        onSelected={(code) => {
          setCountry(code)
        }}
        title={gettext("Choose your organization country")}
        value={country}
      />
      <IonItemGroup>
        <IonItem lines="none">
          <IonInput
            disabled={!allowVatEntry}
            helperText={(allowVatEntry)
              ? gettext("Click or tap to change VAT number.")
              : gettext("Not applicable.")
            }
            label={gettext("VAT number")}
            maxlength={24}
            labelPlacement={props.labelPlacement || "stacked"}
            onClick={() => setVatNumber(null)}
            readonly
            value={(allowVatEntry) ? vatNumber : null}
          />
        </IonItem>
      </IonItemGroup>
    </>
  )
}


export { VATCountrySelector }